import {
	models,
	type PricingTier,
	type ProviderModelMapping,
} from "./models.js";
import { providers, type ServiceTier } from "./providers.js";
import { expandAllProviderRegions } from "./region-helpers.js";

/**
 * Check if a specific model and provider combination supports streaming.
 * When a region is specified, checks the expanded regional mapping.
 */
export function getModelStreamingSupport(
	modelName: string,
	providerId?: string,
	region?: string,
): boolean | "only" | null {
	// When a provider is specified, prefer the model definition that includes it
	const modelInfo = providerId
		? (models.find(
				(m) =>
					m.id === modelName &&
					m.providers.some((p) => p.providerId === providerId),
			) ?? models.find((m) => m.id === modelName))
		: models.find((m) => m.id === modelName);
	if (!modelInfo) {
		return null;
	}

	// Expand regions so we can match region-specific streaming overrides
	const expanded = expandAllProviderRegions(modelInfo.providers);

	// If no specific provider is requested, check if any provider for this model supports streaming
	if (!providerId) {
		return expanded.some((provider) => provider.streaming !== false);
	}

	// Check specific provider (and region) for this model, falling back to the
	// root mapping without region
	const providerMapping =
		expanded.find(
			(p) =>
				p.providerId === providerId &&
				(region ? p.region === region : !p.region),
		) ?? expanded.find((p) => p.providerId === providerId && !p.region);
	return providerMapping?.streaming ?? false;
}

function getProviderMappingForModel(
	modelName: string,
	providerId: string,
	region?: string | null,
): ProviderModelMapping | undefined {
	const modelInfo = models.find((m) => m.id === modelName);
	if (!modelInfo) {
		return undefined;
	}

	const expandedProviders = expandAllProviderRegions(
		modelInfo.providers as ProviderModelMapping[],
	);
	const providerEntries = expandedProviders.filter(
		(p) => p.providerId === providerId,
	);
	const isBaseEntry = (p: ProviderModelMapping) =>
		p.region === undefined || p.region === null;
	const hasRegionalEntries = providerEntries.some((p) => !isBaseEntry(p));

	if (region !== null && region !== undefined) {
		const regionalEntry = providerEntries.find((p) => p.region === region);
		if (regionalEntry) {
			return regionalEntry;
		}
		if (!hasRegionalEntries) {
			return providerEntries.find(isBaseEntry);
		}
		return undefined;
	}

	return providerEntries.find(isBaseEntry) ?? providerEntries[0];
}

export function getSupportedServiceTiers(
	modelName: string,
	providerId: string,
	region?: string | null,
): ServiceTier[] {
	const providerDefinition = providers.find((p) => p.id === providerId);
	const providerTiers = providerDefinition?.serviceTiers ?? [];
	if (providerTiers.length === 0) {
		return [];
	}

	const providerMapping = getProviderMappingForModel(
		modelName,
		providerId,
		region,
	);
	const supportedTierIds = providerMapping?.serviceTiers;
	if (!supportedTierIds || supportedTierIds.length === 0) {
		return [];
	}

	const serviceTierRegions = providerMapping.serviceTierRegions;
	if (serviceTierRegions && serviceTierRegions.length > 0) {
		const effectiveRegion =
			region ?? (serviceTierRegions.includes("global") ? "global" : undefined);
		if (!effectiveRegion || !serviceTierRegions.includes(effectiveRegion)) {
			return [];
		}
	}

	const supportedTierIdSet = new Set(supportedTierIds);
	return providerTiers
		.filter((tier) => supportedTierIdSet.has(tier.id))
		.map((tier) => ({
			...tier,
			multiplier:
				providerMapping.serviceTierMultipliers?.[tier.id] ?? tier.multiplier,
		}));
}

export function supportsServiceTier(
	modelName: string,
	providerId: string,
	tierId: string | null | undefined,
	region?: string | null,
): boolean {
	if (!tierId) {
		return false;
	}
	return getSupportedServiceTiers(modelName, providerId, region).some(
		(tier) => tier.id === tierId,
	);
}

// OpenAI prompt_cache_retention="24h" eligibility per
// https://developers.openai.com/api/docs/guides/prompt-caching.
// gpt-5.5 and gpt-5.5-pro default to 24h and reject "in_memory"; the rest
// accept either. Models not on this list only support "in_memory" caching.
const OPENAI_EXTENDED_PROMPT_CACHE_MODELS = new Set<string>([
	"gpt-5.5",
	"gpt-5.5-pro",
	"gpt-5.4",
	"gpt-5.2",
	"gpt-5.1-codex-max",
	"gpt-5.1",
	"gpt-5.1-codex",
	"gpt-5.1-codex-mini",
	"gpt-5.1-chat-latest",
	"gpt-5",
	"gpt-5-codex",
	"gpt-4.1",
]);

export function supportsOpenAIExtendedPromptCache(modelName: string): boolean {
	return OPENAI_EXTENDED_PROMPT_CACHE_MODELS.has(modelName);
}

// OpenAI explicit prompt caching (`prompt_cache_options` +
// `prompt_cache_breakpoint` content markers) per
// https://developers.openai.com/api/docs/guides/prompt-caching.
// Only GPT-5.6 and later model families support these fields; older models
// reject them with a 400, so they must only be forwarded for this list.
const OPENAI_EXPLICIT_PROMPT_CACHE_MODELS = new Set<string>([
	"gpt-5.6-sol",
	"gpt-5.6-terra",
	"gpt-5.6-luna",
	"gpt-6-astra",
	"gpt-6-sol",
	"gpt-6.1-sol",
	"gpt-6.1-terra",
	"gpt-6-luna",
]);

export function supportsOpenAIExplicitPromptCache(modelName: string): boolean {
	return OPENAI_EXPLICIT_PROMPT_CACHE_MODELS.has(modelName);
}

export type PricingPeriod = "peak" | "off_peak";

/**
 * Which `peakPricing` period applies to a mapping at a given instant (UTC).
 * A matching `offPeakDaysUtc` day overrides the hourly windows. Undefined when
 * the mapping has no time-based pricing.
 */
export function resolvePricingPeriod(
	mapping: Pick<ProviderModelMapping, "peakPricing">,
	now: Date = new Date(),
): PricingPeriod | undefined {
	const peakPricing = mapping.peakPricing;
	if (!peakPricing) {
		return undefined;
	}
	if (peakPricing.offPeakDaysUtc?.includes(now.getUTCDay())) {
		return "off_peak";
	}
	const hour = now.getUTCHours();
	return peakPricing.hoursUtc.some(
		([start, end]) => hour >= start && hour < end,
	)
		? "peak"
		: "off_peak";
}

/**
 * A context-length tier's per-token rates for a pricing period: its
 * `peakPricing` rates for that period when it has them, its flat rates
 * otherwise. `period` is undefined when no time-based pricing applies.
 */
export function resolveTierTimeBasedPricing(
	tier: Pick<
		PricingTier,
		"inputPrice" | "outputPrice" | "cachedInputPrice" | "peakPricing"
	>,
	period: PricingPeriod | undefined,
): {
	inputPrice: string;
	outputPrice: string;
	cachedInputPrice: string | undefined;
	pricingPeriod: PricingPeriod | undefined;
} {
	if (period && tier.peakPricing) {
		const rates =
			period === "peak" ? tier.peakPricing.peak : tier.peakPricing.offPeak;
		return {
			inputPrice: rates.inputPrice,
			outputPrice: rates.outputPrice,
			cachedInputPrice: rates.cachedInputPrice,
			pricingPeriod: period,
		};
	}
	return {
		inputPrice: tier.inputPrice,
		outputPrice: tier.outputPrice,
		cachedInputPrice: tier.cachedInputPrice,
		pricingPeriod: undefined,
	};
}

/**
 * Resolve the per-token rates that apply to a mapping at a given instant.
 * Without `peakPricing`, the mapping's base inputPrice/outputPrice/
 * cachedInputPrice are always returned. With `peakPricing`, the `peak` rates
 * apply while `now` (UTC) falls inside a peak window and the `offPeak` rates
 * otherwise. A matching `offPeakDaysUtc` day overrides the hourly windows.
 */
export function resolveTimeBasedPricing(
	mapping: Pick<
		ProviderModelMapping,
		"inputPrice" | "outputPrice" | "cachedInputPrice" | "peakPricing"
	>,
	now: Date = new Date(),
): {
	inputPrice: string;
	outputPrice: string;
	cachedInputPrice: string | undefined;
} {
	const peakPricing = mapping.peakPricing;
	const period = resolvePricingPeriod(mapping, now);
	if (!peakPricing || !period) {
		return {
			inputPrice: mapping.inputPrice ?? "0",
			outputPrice: mapping.outputPrice ?? "0",
			cachedInputPrice: mapping.cachedInputPrice,
		};
	}
	const tier = period === "peak" ? peakPricing.peak : peakPricing.offPeak;
	return {
		inputPrice: tier.inputPrice,
		outputPrice: tier.outputPrice,
		cachedInputPrice: tier.cachedInputPrice,
	};
}

/**
 * Whether the static catalogue maps `modelName` (by id or alias) for
 * `providerId`. With `activeOnly`, deactivated mappings do not count — that
 * variant is the routing/listing rule for Airside carrier listings:
 * deactivating a static mapping hands the model over to the carrier's listing.
 */
export function staticCatalogueMapsModel(
	providerId: string,
	modelName: string,
	options: { activeOnly?: boolean } = {},
): boolean {
	const now = new Date();
	return models.some(
		(model) =>
			(model.id === modelName ||
				("aliases" in model &&
					(model.aliases as readonly string[] | undefined)?.includes(
						modelName,
					))) &&
			model.providers.some((mapping) => {
				if (mapping.providerId !== providerId) {
					return false;
				}
				if (!options.activeOnly) {
					return true;
				}
				const deactivatedAt =
					"deactivatedAt" in mapping
						? (mapping.deactivatedAt as Date | string | undefined)
						: undefined;
				return !(deactivatedAt && new Date(deactivatedAt) <= now);
			}),
	);
}
